import { jsx as _jsx } from "react/jsx-runtime";
import { useAuth } from "@madmedical/store";
import { usePersistentState } from "@madmedical/storage";
import { useEffect } from "react";
import { useIsCurrentRouteNha, useIsCurrentRouteOpen, } from "@madmedical/config";
import { useGetMeQuery } from "./api";
import { RepresentedPatientProvider } from "./representedPatient";
import useRouteNavigate from "./routing/useRouteNavigate";
import { NonDetachedPatientIdProvider } from "./detachedPatientId";
const REPRESENTED_PATIENT_KEY = "representedPatient";
/**
 * Preloading current user and provides represented patient. Goes under AuthWrapper
 */
const UserWrapper = ({ children }) => {
    const { isTokenAvailable } = useAuth();
    const { data: meData, isLoading: isMeLoading } = useGetMeQuery(undefined, {
        skip: !isTokenAvailable,
    });
    const isOpenRoute = useIsCurrentRouteOpen();
    const isNhaRoute = useIsCurrentRouteNha();
    const [representedPatient, setRepresentedPatient, { isLoading: isRepresentedLoading },] = usePersistentState(REPRESENTED_PATIENT_KEY, null);
    const navigate = useRouteNavigate();
    const [onboardingShown, setOnboardingShown, { isLoading: isOnboardingLoading },] = usePersistentState("onboardingDismissed", false);
    const isLoading = isMeLoading || isRepresentedLoading;
    const needsRepresentatedSelect = isNhaRoute &&
        (meData === null || meData === void 0 ? void 0 : meData.roles.isRepresentative) &&
        !isRepresentedLoading &&
        !representedPatient;
    // Select the latest representative
    useEffect(() => {
        var _a, _b;
        if (isLoading) {
            return;
        }
        if (!needsRepresentatedSelect) {
            return;
        }
        const defaultRepresented = (_a = meData.patient) !== null && _a !== void 0 ? _a : (_b = meData.representative) === null || _b === void 0 ? void 0 : _b.representedPatients[meData.representative.representedPatients.length - 1];
        if (!defaultRepresented) {
            return;
        }
        setRepresentedPatient(defaultRepresented);
    }, [isLoading, needsRepresentatedSelect, meData, setRepresentedPatient]);
    // Redirect new patient users to onboarding
    useEffect(() => {
        if (isOpenRoute) {
            // Do not enforce on public routes
            return;
        }
        if (needsRepresentatedSelect) {
            // Wait until patient is selected
            return;
        }
        if (!isNhaRoute && (isOnboardingLoading || onboardingShown)) {
            // Do not enforce on non-NHA route if it was already shown once
            return;
        }
        const currentPatient = (meData === null || meData === void 0 ? void 0 : meData.roles.isRepresentative)
            ? representedPatient
            : meData === null || meData === void 0 ? void 0 : meData.patient;
        if (!currentPatient) {
            return;
        }
        if (!(currentPatient === null || currentPatient === void 0 ? void 0 : currentPatient.onboarded)) {
            setOnboardingShown(true);
            navigate("onboarding", {});
        }
    }, [
        isOpenRoute,
        needsRepresentatedSelect,
        isNhaRoute,
        isOnboardingLoading,
        onboardingShown,
        representedPatient,
        meData,
        setOnboardingShown,
        navigate,
    ]);
    // Clean up on logout
    useEffect(() => {
        if (isTokenAvailable) {
            return;
        }
        setRepresentedPatient(null);
        setOnboardingShown(false);
    }, [isTokenAvailable, setRepresentedPatient, setOnboardingShown]);
    if ((isLoading || needsRepresentatedSelect) && isNhaRoute && !isOpenRoute) {
        return null;
    }
    return (_jsx(NonDetachedPatientIdProvider, { children: _jsx(RepresentedPatientProvider, Object.assign({ value: {
                representedPatient,
                setRepresentedPatient,
            } }, { children: children })) }));
};
export default UserWrapper;
